import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

// Thème clair, sombre ou celui du système, et menu latéral replié : préférences d'affichage
// appliquées tout de suite, mémorisées sur l'appareil puis synchronisées avec le compte.

export const THEMES = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEMES)[number];

const THEME_KEY = 'ba.theme';
const SIDEBAR_KEY = 'ba.sidebar';

interface Display {
  theme: ThemePreference;
  /** Thème effectivement appliqué (le système tranche pour `system`). */
  resolved: 'light' | 'dark';
  setTheme: (theme: ThemePreference) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const DisplayContext = createContext<Display | null>(null);

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // stockage indisponible : préférence non mémorisée sur l'appareil
  }
}

const systemDark = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

export function DisplayProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(() => {
    const stored = read(THEME_KEY);
    return (THEMES as readonly string[]).includes(stored ?? '')
      ? (stored as ThemePreference)
      : 'system';
  });
  const [prefersDark, setPrefersDark] = useState(systemDark);
  const [sidebarCollapsed, setCollapsed] = useState(() => read(SIDEBAR_KEY) === 'collapsed');
  const resolved = theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme;

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    // Transitions activées après le premier rendu : pas de flash au chargement.
    const frame = requestAnimationFrame(() => root.classList.add('theme-ready'));
    return () => cancelAnimationFrame(frame);
  }, [resolved]);

  const value = useMemo<Display>(
    () => ({
      theme,
      resolved,
      setTheme: (next) => {
        setThemeState(next);
        write(THEME_KEY, next);
      },
      sidebarCollapsed,
      setSidebarCollapsed: (next) => {
        setCollapsed(next);
        write(SIDEBAR_KEY, next ? 'collapsed' : 'expanded');
      },
    }),
    [theme, resolved, sidebarCollapsed],
  );
  return <DisplayContext.Provider value={value}>{children}</DisplayContext.Provider>;
}

export function useDisplay(): Display {
  const value = useContext(DisplayContext);
  if (!value) throw new Error('useDisplay hors de DisplayProvider');
  return value;
}
